/**
 * @typedef {{
 *   title: string;
 *   board_id: number;
 * }} Board
 * @typedef {{
 *   title: ReactDOM;
 *   boards: Board[];
 * }} Group
 * @typedef {Group[]} BoardTree
 * @typedef {{
 *   board: Board;
 *   boardIndex: number;
 *   group: Group;
 *   groupIndex: number;
 *   boardTree: BoardTree;
 * }} MaskWhenHoverProps
 */
import useViewModel from "./viewModel";
import styles from "./index.module.less";
import BoardItemStyles from "../BoardItem/index.module.less";
import BoardItem from "../BoardItem";
import classNames from "classnames";
import imageUrl from "../../utils/imageUrl";
import HBIcon from "../HBIcon";

/**
 * @param {{
 *   boards: Board[];
 *   keyword: string;
 *
 *   onlyCreation?: boolean;
 *   className?: string;
 *
 *   selectable?: boolean;
 *   value?: number[];
 *
 *   showCreator?: boolean;
 *
 *   BoardHoverMask?: (props: MaskWhenHoverProps) => ReactDOM;
 *   onCreateBoard?: () => void;
 *   onSelected?: (selected: number[], selectedBoards: Board[]) => void;
 *   canItemShow?: (board: Board) => boolean;
 *   onFilter?: (keyword: string, result: Board[]) => void;
 *   onBoardsUpdate?: (boards: Board[]) => void;
 *   onBoardClick?: (board: Board) => void;
 * }} props
 * @returns {*}
 * @constructor
 */
export default function MyBoardFilter(props) {
  const {
    onCreateBoard,
    BoardHoverMask,
    className,
    keyword,
    selectable,
    SelectedMark,
    showCreator,
    limitByPinCount,
    limitByPrivate,
  } = props;
  const { boardsToRender, selected, onBoardSelect } = useViewModel(props);
  const getLimit = (limitByPinCount, limitByPrivate, pinCount, isPrivate) => {
    if (limitByPrivate && isPrivate) {
      return true;
    }
    if (limitByPinCount > 0 && pinCount < limitByPinCount) {
      return true;
    }
    return false;
  };

  return (
    <div
      className={classNames(styles.boards, className, {
        [styles.selectable]: selectable,
      })}
    >
      {boardsToRender.map((group, idx) => (
        <div className={styles.group} key={group.title + idx}>
          <div className={styles.groupName}>{group.title}</div>
          {group.boards.map((board, index) => {
            const { board_id, title, is_private, cover, pins, pin_count } =
              board;
            const file = cover ? cover.file : pins[0]?.file;
            const coverUrl = file && imageUrl(file, { width: 40 });
            const isSelected = selected.includes(board_id);
            const isPrivate = is_private !== 0 && is_private !== 1;
            return (
              <div
                className={styles.groupItem}
                key={board_id}
                data-board-id={board_id}
                data-gd-click="button_click"
                data-button-name="采下来"
              >
                <BoardItem
                  className={classNames(BoardItemStyles.boardItem, {
                    [styles.selected]: isSelected,
                    [styles.isOnly]: getLimit(
                      limitByPinCount,
                      limitByPrivate,
                      pin_count,
                      isPrivate
                    ),
                  })}
                  id={board_id}
                  title={title}
                  coverUrl={coverUrl}
                  isPrivate={isPrivate}
                  onClick={onBoardSelect(
                    board,
                    getLimit(
                      limitByPinCount,
                      limitByPrivate,
                      pin_count,
                      isPrivate
                    )
                  )}
                  pinCount={pin_count}
                  limitByPinCount={limitByPinCount}
                  limitByPrivate={limitByPrivate}
                />
                <div className={styles.boardHoverMask}>
                  {BoardHoverMask && (
                    <BoardHoverMask
                      board={board}
                      boardIndex={index}
                      group={group}
                      groupIndex={idx}
                      boardsGroups={boardsToRender}
                    />
                  )}
                </div>
                {!!(isSelected && SelectedMark) &&
                  !getLimit(
                    limitByPinCount,
                    limitByPrivate,
                    pin_count,
                    isPrivate
                  ) && (
                    <SelectedMark
                      board={board}
                      boardIndex={index}
                      group={group}
                      groupIndex={idx}
                      boardsGroups={boardsToRender}
                    />
                  )}
              </div>
            );
          })}
        </div>
      ))}

      {!!boardsToRender.length && !!keyword && (showCreator ?? true) && (
        <div className={styles.createBoardPanelSpliter} />
      )}

      {!!keyword && (showCreator ?? true) && (
        <div
          className={styles.createBoardPanel}
          onClick={onCreateBoard}
          data-gd-click="button_click"
          data-button-name="创建画板"
        >
          <HBIcon className={styles.createBoardBtn} type="add" />
          <span style={{ fontWeight: "bolder" }}>创建画板</span>
          <span>{keyword}</span>
        </div>
      )}
    </div>
  );
}
